import React from 'react';
import { Box, Typography, Avatar, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Button } from '@material-ui/core';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import ReportView from './ReportView';

// Premium AI Icon based on Amplo branding
const AgentIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L4.5 20.29L5.21 21L12 18L18.79 21L19.5 20.29L12 2Z" fill="currentColor" />
        <path d="M12 18V6L18 19L12 18Z" fill="white" fillOpacity="0.3" />
    </svg>
);

const MarkdownTable = ({ tableStr }) => {
    // Basic table parser
    const lines = tableStr.trim().split('\n').filter(l => l.includes('|'));
    if (lines.length < 2) return null;

    const rows = lines.map(line =>
        line.split('|')
            .filter((cell, idx, arr) => idx > 0 && idx < arr.length - 1)
            .map(cell => cell.trim())
    );

    // Skip separator rows (e.g. |:---|---:|)
    const dataRows = rows.filter(row => !row.every(cell => /^[ \-:]+$/.test(cell)));
    if (dataRows.length < 1) return null;

    const headers = dataRows[0];
    const bodyRows = dataRows.slice(1);

    return (
        <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e2e8f0', my: 3, borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff', marginBottom: '24px' }}>
            <Table size="small">
                <TableHead style={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                        {headers.map((h, i) => (
                            <TableCell key={i} style={{ fontWeight: 800, color: '#1e293b', fontSize: '13px', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {bodyRows.map((row, i) => (
                        <TableRow key={i} hover style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fcfdfe' }}>
                            {row.map((cell, j) => {
                                const isPositive = cell.includes('delta-positive');
                                return (
                                    <TableCell key={j} style={{ fontSize: '13px', color: '#334155' }}>
                                        {isPositive ? (
                                            <Box component="span" style={{ color: '#10b981', fontWeight: 700, backgroundColor: '#f0fdf4', padding: '2px 8px', borderRadius: '4px' }}>
                                                {cell.replace(/<.*?>/g, '')}
                                            </Box>
                                        ) : cell.replace(/<.*?>/g, '')}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const AmploChart = ({ config }) => {
    let parsed;
    try {
        parsed = JSON.parse(config);
    } catch (e) {
        return <pre style={{ background: '#f8fafc', padding: 16 }}>{config}</pre>;
    }

    const data = (parsed.data?.labels || []).map((label, idx) => ({
        name: label,
        value: parsed.data?.values?.[idx] || 0
    }));

    return (
        <Box my={4} p={3} bgcolor="#fff" borderRadius="16px" border="1px solid #e2e8f0" style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#64748b', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {parsed.title}
            </Typography>
            <Box height={350} width="100%">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            interval={0}
                            angle={-25}
                            textAnchor="end"
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                        <RechartsTooltip
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
                        />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={40}>
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.name === parsed.highlight ? '#2563eb' : '#cbd5e1'} />
                            ))}
                        </Bar>
                        {(parsed.referenceLines || []).map((line, i) => (
                            <ReferenceLine
                                key={i}
                                y={line.value}
                                stroke={line.color}
                                strokeDasharray="5 5"
                                label={{ position: 'right', value: line.label, fill: line.color, fontSize: 10, fontWeight: 700 }}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

const ChatMessage = ({ message, onViewDetails }) => {
    const isUser = message.role === 'user';

    const renderBlock = (content) => {
        if (!content && content !== 0) return null;

        // Ensure content is always a string (e.g. slider values come as numbers)
        const text = typeof content === 'string' ? content : String(content);

        // Split into Code Blocks, Tables, and Regular Text
        const blocks = text.split(/(```[\s\S]*?```|\|(?:[^\n]*\|)+\n(?:\|(?:\s*:?-+:?\s*\|)+\n)?(?:\|(?:[^\n]*\|)+\n?)*)/g);

        return blocks.map((block, idx) => {
            if (!block.trim()) return null;

            // Handle Code Blocks
            if (block.startsWith('```')) {
                const codeContent = block.replace(/```(amplo-chart)?/g, '').trim();
                if (block.includes('amplo-chart')) {
                    return <AmploChart key={idx} config={codeContent} />;
                }
                return <pre key={idx} style={{ background: '#0f172a', color: '#e2e8f0', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '13px' }}>{codeContent}</pre>;
            }

            // Handle Tables
            if (block.trim().startsWith('|')) {
                return <MarkdownTable key={idx} tableStr={block} />;
            }

            // Handle Regular Text with Headers and Inline styles
            const lines = block.split('\n');
            return lines.map((line, lIdx) => {
                const trimmed = line.trim();
                if (!trimmed && lIdx > 0) return <Box key={lIdx} height={12} />;

                if (trimmed.startsWith('## ')) {
                    return <Typography key={lIdx} variant="h6" style={{ fontWeight: 800, color: isUser ? '#fff' : '#0f172a', marginTop: '24px', marginBottom: '12px', borderBottom: isUser ? 'none' : '2px solid #f1f5f9', pb: 1 }}>{trimmed.replace('## ', '')}</Typography>;
                }

                if (trimmed.startsWith('### ')) {
                    return <Typography key={lIdx} variant="subtitle1" style={{ fontWeight: 800, color: isUser ? '#fff' : '#1e293b', marginTop: '16px', marginBottom: '8px' }}>{trimmed.replace('### ', '')}</Typography>;
                }

                // Inline Parsing (Bold, Lists)
                const parts = line.split(/(\*\*.*?\*\*|- .*)/g);
                return (
                    <Typography key={lIdx} variant="body1" component="div" style={{
                        lineHeight: 1.7,
                        marginBottom: '10px',
                        color: isUser ? '#f1f5f9' : '#334155',
                        fontWeight: 500
                    }}>
                        {parts.map((part, pIdx) => {
                            if (!part) return null;
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={pIdx} style={{ fontWeight: 800, color: isUser ? '#fff' : '#0f172a' }}>{part.slice(2, -2)}</strong>;
                            }
                            if (part.startsWith('- ')) {
                                return <Box key={pIdx} display="flex" pl={2} mb={0.5}><Box component="span" mr={1.5} style={{ color: isUser ? '#60a5fa' : '#3b82f6' }}>•</Box> {part.slice(2).trim()}</Box>;
                            }
                            return part;
                        })}
                    </Typography>
                );
            });
        });
    };

    const renderContent = (content) => {
        if (!content && content !== 0) return '';

        // ESG Analysis Report special view or PDF report
        const contentStr = typeof content === 'string' ? content : String(content);
        const isEsgHeader = contentStr.trim().startsWith('# ESG ANALYSIS REPORT');
        const isMsplPdfReport = message?.isMsplEsgReport || message?.pdfReportUrl || contentStr.includes('MSPL Enterprise Strategic Assessment') || contentStr.includes('MSPL-ESG-Report.pdf') || contentStr.includes('MSPL=ESG-Report.pdf');
        if (isEsgHeader || isMsplPdfReport) {
            return (
                <ReportView
                    content={contentStr}
                    pdfReportUrl={message?.pdfReportUrl || (isMsplPdfReport ? '/MSPL-ESG-Report.pdf' : null)}
                />
            );
        }

        // Check for JSON-style parameters synthesis
        if (typeof content === 'string' && content.trim().startsWith('{') && !content.includes('\n')) {
            try {
                const parsed = JSON.parse(content);
                if (typeof parsed === 'object' && parsed !== null) {
                    return (
                        <Box>
                            <Typography variant="overline" style={{ color: isUser ? '#bfdbfe' : '#64748b', fontWeight: 800, letterSpacing: '1px', display: 'block', marginBottom: '10px', borderBottom: isUser ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                Parameter Synthesis
                            </Typography>
                            {Object.entries(parsed).map(([key, value]) => (
                                <Box key={key} display="flex" mt={1}>
                                    <Typography variant="body2" style={{ fontWeight: 700, marginRight: '8px', minWidth: '80px', color: isUser ? '#fff' : '#1e293b' }}>
                                        {key}:
                                    </Typography>
                                    <Typography variant="body2" style={{ color: isUser ? '#dbeafe' : '#475569', fontWeight: 500 }}>
                                        {String(value)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                }
            } catch (e) { }
        }

        return renderBlock(content);
    };

    return (
        <Box
            display="flex"
            justifyContent={isUser ? "flex-end" : "flex-start"}
            mb={3}
            width="100%"
        >
            {!isUser && (
                <Box mr={2} mt={0.5} style={{ position: 'relative' }}>
                    <Avatar
                        style={{
                            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                            color: '#fff',
                            width: 38,
                            height: 38,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}
                    >
                        <AgentIcon />
                    </Avatar>
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: 2,
                            right: -2,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#10b981',
                            border: '2px solid #fff',
                            boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)'
                        }}
                    />
                </Box>
            )}

            <Box maxWidth="80%">
                <Paper
                    elevation={0}
                    style={{
                        padding: '16px 20px',
                        borderRadius: isUser ? '20px 20px 4px 20px' : '4px 20px 20px 20px',
                        background: isUser ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : '#ffffff',
                        color: isUser ? '#fff' : '#334155',
                        boxShadow: isUser ? '0 10px 25px -5px rgba(15, 23, 42, 0.2)' : '0 4px 20px -5px rgba(0,0,0,0.05)',
                        border: isUser ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0',
                    }}
                >
                    {renderContent(message.content)}

                    {!isUser && message.uuid && (
                        <Box mt={3} pt={2} borderTop="1px solid #f1f5f9" display="flex" justifyContent="flex-end">
                            <Button
                                size="small"
                                onClick={() => onViewDetails && onViewDetails(message.uuid)}
                                style={{
                                    textTransform: 'none',
                                    color: '#3b82f6',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    padding: '4px 12px',
                                    backgroundColor: '#eff6ff'
                                }}
                            >
                                View Analysis Details →
                            </Button>
                        </Box>
                    )}
                </Paper>
                <Typography variant="caption" style={{ display: 'block', textAlign: isUser ? 'right' : 'left', mt: 1, color: '#94a3b8', fontSize: '10px', marginTop: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {isUser ? 'Verified User' : 'Amplo AI Intelligence'}
                </Typography>
            </Box>

            {isUser && (
                <Box ml={2} mt={0.5}>
                    <Avatar
                        style={{
                            backgroundColor: '#eff6ff',
                            color: '#1e40af',
                            width: 38,
                            height: 38,
                            fontSize: '14px',
                            fontWeight: 800,
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.08)',
                            border: '1.5px solid #dbeafe'
                        }}
                    >
                        U
                    </Avatar>
                </Box>
            )}
        </Box>
    );
};

export default ChatMessage;
